import { ICoreContext } from '@msdyn365-commerce/core';
import { Row } from '@tanstack/react-table';
import React from 'react';
import { IOrderHistoryLine, IOrderHistoryTableData } from '../../../actions/order-history/get-order-history.action';
import DataTitle from '../../../components/data-title';
import { NO_DATA_FALLBACK } from '../../../configuration';
import { ILocalizationState } from '../../../states/localization-state';
import {
    checkIfValidDate,
    checkIfValidNumber,
    dateLocaleFormatter,
    formatCurrency,
    formatNumberLocaleStatic,
    formatOverallStatusCodeForTooltip,
    formatStatusCodeForTooltip,
    mapItemInvoiceStatusCodeDescriptionToText,
    mapItemInvoiceStatusCodeToText,
    mapItemStatusCodeDescriptionToText,
    mapItemStatusCodeToText,
    toLocalDate
} from '../../../utilities';
import { displayTableCellValue } from '../../../utilities/display-table-cell-value';
import { IOrderHistoryResources } from '../order-history.props.autogenerated';

interface OrderHistoryTableExpandedRowProps {
    resources: IOrderHistoryResources;
    isEven: boolean;
    row: Row<IOrderHistoryTableData>;
    globalFilter: string;
    context: ICoreContext;
    localizationState: ILocalizationState;
    CLASS_NAME: string;
}

interface LatestItemValues {
    status?: string;
    quantity?: string;
    date?: string;
    unit?: string;
    info?: string;
}

const OrderHistoryTableExpandedRow: React.FC<OrderHistoryTableExpandedRowProps> = ({
    resources,
    isEven,
    row,
    globalFilter,
    context,
    localizationState,
    CLASS_NAME
}) => {
    const { OrderLines } = row.original;

    const getFirstScheduleLine = (orderLine: IOrderHistoryLine) => {
        return orderLine.ScheduleLines && orderLine.ScheduleLines.length > 0 ? orderLine.ScheduleLines[0] : null;
    };

    const getPreferredInfo = (orderLine: IOrderHistoryLine, resources: IOrderHistoryResources): LatestItemValues => {
        const firstScheduleLine = getFirstScheduleLine(orderLine);
        const status = mapItemStatusCodeToText(orderLine.StateLogistics, resources);

        switch (status) {
            case resources.globalStatusItemRequested:
                return {
                    status,
                    date: orderLine.RequestedDate,
                    quantity: orderLine.RequestedQty,
                    unit: orderLine.RequestedUnit
                };
            case resources.globalStatusItemConfirmed:
                return {
                    status,
                    date: firstScheduleLine?.ConfirmedDate,
                    quantity: firstScheduleLine?.ConfirmedQty,
                    unit: firstScheduleLine?.ConfirmedUnit
                };
            case resources.globalStatusItemTransportPlanned:
                return {
                    status,
                    date: firstScheduleLine?.ConfirmedDate,
                    quantity: firstScheduleLine?.ConfirmedQty,
                    unit: firstScheduleLine?.ConfirmedUnit
                };
            case resources.globalStatusItemGoodsDispatched:
                return {
                    status,
                    date: firstScheduleLine?.ConfirmedDate,
                    quantity: firstScheduleLine?.ConfirmedQty,
                    unit: firstScheduleLine?.ConfirmedUnit
                };
            case resources.globalStatusItemGoodsReceived:
                return {
                    status,
                    date: orderLine.ActualArrivalDate,
                    quantity: firstScheduleLine?.ConfirmedQty,
                    unit: firstScheduleLine?.ConfirmedUnit
                };
            default:
                return { status };
        }
    };

    const renderDateRow = (orderLine: IOrderHistoryLine): string => {
        const latestEvent = getPreferredInfo(orderLine, resources);
        const dateToRender = [latestEvent.date].find(d => checkIfValidDate(d));

        if (!dateToRender) {
            return NO_DATA_FALLBACK;
        }

        const localDate = toLocalDate(dateToRender);
        return dateLocaleFormatter(localizationState).formatDate(localDate);
    };

    const renderQtyRow = (orderLine: IOrderHistoryLine): string => {
        const latestInfo = getPreferredInfo(orderLine, resources);

        if (latestInfo.quantity && checkIfValidNumber(latestInfo.quantity)) {
            return `${formatNumberLocaleStatic(latestInfo.quantity, localizationState)} ${latestInfo.unit ?? ''}`;
        }

        return NO_DATA_FALLBACK;
    };

    const TableBody: React.FC = () => {
        return (
            <tbody>
                {OrderLines.map((orderLine: IOrderHistoryLine, index: number) => {
                    return (
                        <React.Fragment key={index}>
                            <tr>
                                <td>{displayTableCellValue(orderLine.LineNumber, globalFilter)}</td>
                                <td>{displayTableCellValue(orderLine.ProductName, globalFilter)}</td>
                                <td>{displayTableCellValue(orderLine.ProductNumber, globalFilter)}</td>
                                <td>{displayTableCellValue(orderLine.CustomerProductNumber, globalFilter)}</td>
                                <td>
                                    <DataTitle
                                        label={displayTableCellValue(
                                            mapItemStatusCodeToText(orderLine.StateLogistics, resources),
                                            globalFilter
                                        )}
                                        title={mapItemStatusCodeDescriptionToText(orderLine.StateLogistics, resources)}
                                    />
                                </td>
                                <td>{renderDateRow(orderLine)}</td>
                                <td>{renderQtyRow(orderLine)}</td>
                                <td>
                                    <DataTitle
                                        label={displayTableCellValue(
                                            mapItemInvoiceStatusCodeToText(orderLine.StateInvoice, resources),
                                            globalFilter
                                        )}
                                        title={mapItemInvoiceStatusCodeDescriptionToText(orderLine.StateInvoice, resources)}
                                    />
                                </td>
                                <td>
                                    {orderLine.Currency
                                        ? formatCurrency(localizationState, orderLine.Currency, Number(orderLine.NetValue))
                                        : NO_DATA_FALLBACK}
                                </td>
                            </tr>
                        </React.Fragment>
                    );
                })}
            </tbody>
        );
    };

    const TableHead: React.FC = () => {
        return (
            <>
                <thead>
                    <tr>
                        <th>{resources.orderHistoryDetailsLineNumber}</th>
                        <th>{resources.orderHistoryDetailsProductName}</th>
                        <th>{resources.orderHistoryDetailsProductNumber}</th>
                        <th>{resources.orderHistoryDetailsCustomerProductNumber}</th>
                        <th>
                            <span className='lxs-data-title__inline'>
                                {resources.orderHistorySalesOrderDeliveryStatus}{' '}
                                <DataTitle
                                    label={<span className='lxs-data-title__info-icon'></span>}
                                    title={formatStatusCodeForTooltip(resources, context)}
                                />
                            </span>
                        </th>
                        <th>{resources.orderHistoryDetailsDate}</th>
                        <th>{resources.orderHistoryDetailsQuantity}</th>
                        <th>
                            <span className='lxs-data-title__inline'>
                                {resources.orderHistorySalesOrderInvoiceStatus}{' '}
                                <DataTitle
                                    label={<span className='lxs-data-title__info-icon'></span>}
                                    title={formatOverallStatusCodeForTooltip(resources, context)}
                                />
                            </span>
                        </th>
                        <th>
                            <span className='lxs-data-title__inline'>
                                {resources.orderHistoryDetailsNetValue}{' '}
                                <DataTitle
                                    label={<span className='lxs-data-title__info-icon'></span>}
                                    title={resources.globalTableNetValueToolTip}
                                />
                            </span>
                        </th>
                    </tr>
                </thead>
            </>
        );
    };

    return (
        <tr className={`child ${isEven ? 'even-parent' : 'odd-parent'}`}>
            <td colSpan={row.getVisibleCells().length}>
                <div className={`${CLASS_NAME}__table-orderlines fade-slide-top`}>
                    <table className='table table-sm'>
                        <TableHead />
                        <TableBody />
                    </table>
                </div>
            </td>
        </tr>
    );
};

export default OrderHistoryTableExpandedRow;
